import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, Wallet, ShoppingBag, PackagePlus, Download, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { inr, inr0, fmtDate, downloadCSV } from "@/lib/format";
import { useSettings } from "@/lib/settings";
import { periodPresets, todayISO } from "@/lib/period";
import { PageHeader, KPI, Section, Empty, PeriodPicker } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";
import { ExpenseForm } from "@/pages/Expenses";
import { PayVendorModal } from "@/pages/Purchases";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "daybook", label: "Day Book" },
  { id: "receivables", label: "Receivables" },
  { id: "payables", label: "Payables" },
];
const DAYBOOK_TYPES = ["Sale", "Receipt", "Refund", "Purchase", "Vendor payment", "Expense"];
const TYPE_COLOR = {
  Sale: "bg-[#EEF2F7] text-[#0E1B2E] border-[#C9D3E0]", Receipt: "bg-[#EBF2EC] text-[#2D5A3C] border-[#B8D7BF]",
  Refund: "bg-[#FBF0EC] text-[#A3411D] border-[#F2C5B6]", Purchase: "bg-[#FEF6E6] text-[#B38E46] border-[#EAD096]",
  "Vendor payment": "bg-[#FBF0EC] text-[#A3411D] border-[#F2C5B6]", Expense: "bg-[#FBF0EC] text-[#A3411D] border-[#F2C5B6]",
};

function Line({ label, value, strong, indent, negative, muted }) {
  return (
    <div className={`flex justify-between py-1.5 ${strong ? "border-t border-[#E6E1D8] mt-1 pt-2.5 font-semibold" : ""} ${indent ? "pl-4" : ""}`}>
      <span className={muted ? "text-[#8C929B]" : strong ? "" : "text-[#5C626A]"}>{label}</span>
      <span className="font-num" style={{ color: strong && value < 0 ? "#A3411D" : undefined }}>{negative ? `(${inr(Math.abs(value))})` : inr(value)}</span>
    </div>
  );
}

function Overview({ a }) {
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI testid="acc-net-sales" label="Net Sales" value={inr(a.net_sales)} sub={`${a.invoice_count} invoices · excl. GST`} accent="ink" />
        <KPI testid="acc-gross-profit" label="Gross Profit" value={inr(a.gross_profit)} sub={`${a.gross_margin_pct}% margin on sales`} accent="sage" />
        <KPI testid="acc-expenses" label="Expenses" value={inr(a.expenses)} sub="Running costs, excl. GST" accent="terra" />
        <KPI testid="acc-net-profit" label="Net Profit" value={inr(a.net_profit)} sub="Gross profit − expenses" accent={a.net_profit < 0 ? "terra" : "sage"} />
        <KPI label="Money In" value={inr(a.cash_in)} sub="Customer payments received" accent="sage" />
        <KPI label="Money Out" value={inr(a.cash_out)} sub="Vendors, expenses, refunds" accent="terra" />
        <KPI label="Receivables" value={inr(a.receivables)} sub="Customers owe you (today)" accent="gold" />
        <KPI label="Payables" value={inr(a.payables)} sub="You owe vendors (today)" accent="terra" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Section title="Profit & Loss" className="lg:col-span-2">
          <div className="p-5 text-sm" data-testid="pl-statement">
            <Line label="Gross sales (incl. GST)" value={a.gross_sales} />
            <Line label="Less: GST collected (owed to government)" value={a.gst_output} negative indent />
            <Line label="Net sales" value={a.net_sales} strong />
            <Line label="Less: Cost of goods sold" value={a.cogs} negative indent />
            <Line label="Gross profit" value={a.gross_profit} strong />
            {a.expenses_by_category.map((c) => <Line key={c.category} label={`Less: ${c.category}`} value={c.amount} negative indent />)}
            {a.expenses_by_category.length === 0 && <Line label="Less: Expenses" value={0} negative indent muted />}
            <Line label="Net profit" value={a.net_profit} strong />
            {a.returns_count > 0 && <div className="text-xs text-[#8C929B] mt-3">{a.returns_count} returned invoice{a.returns_count > 1 ? "s" : ""} ({inr(a.returns_value)}) excluded from sales.</div>}
          </div>
        </Section>

        <div className="space-y-4">
          <Section title="GST Summary">
            <div className="p-5 text-sm">
              <Line label="Output GST (on sales)" value={a.gst.output} />
              <Line label="Input GST (purchases)" value={a.purchase_gst} negative indent />
              <Line label="Input GST (expenses)" value={a.expense_gst} negative indent />
              <Line label={a.gst.net_payable >= 0 ? "Net GST payable" : "Input credit carried forward"} value={Math.abs(a.gst.net_payable)} strong />
            </div>
          </Section>
          <Section title="Stock & Balances">
            <div className="p-5 text-sm">
              <Line label="Inventory at cost" value={a.inventory_cost} />
              <Line label="Inventory at retail" value={a.inventory_retail} />
              <Line label="Purchases received (period)" value={a.purchases} />
              {a.vendor_advances > 0 && <Line label="Advances paid to vendors" value={a.vendor_advances} />}
            </div>
          </Section>
        </div>
      </div>

      <Section title="Cash & Bank by Payment Method">
        {a.cash.length === 0 ? <Empty>No money moved in this period</Empty> : (
          <table className="tbl">
            <thead><tr><th>Method</th><th className="num">Money in</th><th className="num">Money out</th><th className="num">Net</th></tr></thead>
            <tbody>
              {a.cash.map((r) => (
                <tr key={r.method}>
                  <td className="font-medium">{r.method}</td>
                  <td className="num font-num text-[#2D5A3C]">{inr(r.in)}</td>
                  <td className="num font-num text-[#A3411D]">{inr(r.out)}</td>
                  <td className="num font-num font-semibold" style={{ color: r.net < 0 ? "#A3411D" : undefined }}>{inr(r.net)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td>Total</td>
                <td className="num font-num">{inr(a.cash_in)}</td>
                <td className="num font-num">{inr(a.cash_out)}</td>
                <td className="num font-num">{inr(a.cash_in - a.cash_out)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function DayBook({ period }) {
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams({ start: period.start, end: period.end });
    if (type) p.set("type", type);
    api.get(`/accounts/daybook?${p}`).then((r) => setD(r.data));
  }, [period, type]);

  const open = (row) => {
    if (row.link?.kind === "invoice") nav(`/invoices/${row.link.id}`);
    else if (row.link?.kind === "po") nav(`/purchases?po=${row.link.id}`);
    else if (row.link?.kind === "expense") nav("/expenses");
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex gap-1 bg-white border border-[#E6E1D8] rounded-full p-1 flex-wrap">
          {["", ...DAYBOOK_TYPES].map((t) => (
            <button key={t || "all"} onClick={() => setType(t)} data-testid={`daybook-type-${t || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs ${type === t ? "bg-[#0E1B2E] text-white" : "text-[#5C626A] hover:bg-[#F4F0EA]"}`}>{t || "All entries"}</button>
          ))}
        </div>
        {d && (
          <div className="ml-auto flex items-center gap-4 text-sm">
            <span className="text-[#2D5A3C]">In <b className="font-num">{inr(d.money_in)}</b></span>
            <span className="text-[#A3411D]">Out <b className="font-num">{inr(d.money_out)}</b></span>
            <button className="btn btn-gold btn-sm" disabled={!d.rows.length}
              onClick={() => downloadCSV(`daybook-${period.start}-to-${period.end}.csv`, ["date", "type", "ref", "party", "description", "method", "amount", "money_in", "money_out"], d.rows)}>
              <Download size={14} /> CSV
            </button>
          </div>
        )}
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th>Ref</th><th>Party</th><th>Details</th><th>Method</th><th className="num">Amount</th><th className="num">In</th><th className="num">Out</th></tr></thead>
            <tbody>
              {d?.rows.map((r, i) => (
                <tr key={i} className="cursor-pointer" onClick={() => open(r)}>
                  <td className="text-[#5C626A] whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td><span className={`chip border ${TYPE_COLOR[r.type] || ""}`}>{r.type}</span></td>
                  <td className="font-num text-xs whitespace-nowrap">{r.ref}</td>
                  <td>{r.party || "—"}</td>
                  <td className="text-[#5C626A] text-xs">{r.description}</td>
                  <td className="text-[#5C626A]">{r.method || ""}</td>
                  <td className="num font-num">{inr(r.amount)}</td>
                  <td className="num font-num text-[#2D5A3C]">{r.money_in != null ? inr(r.money_in) : ""}</td>
                  <td className="num font-num text-[#A3411D]">{r.money_out != null ? inr(r.money_out) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d && d.rows.length === 0 && <Empty>No entries in this period</Empty>}
        </div>
      </div>
    </div>
  );
}

function Receivables() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/invoices").then((r) => setList(r.data.filter((i) => i.balance > 0.01 && !["Void", "Returned", "Draft"].includes(i.status))));
  }, []);
  if (!list) return null;
  const today = todayISO();
  const total = list.reduce((a, i) => a + i.balance, 0);
  const overdue = list.filter((i) => i.due_date && i.due_date < today);
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <KPI label="Total receivable" value={inr(total)} sub={`${list.length} unpaid invoice${list.length === 1 ? "" : "s"}`} accent="gold" />
        <KPI label="Overdue" value={inr(overdue.reduce((a, i) => a + i.balance, 0))} sub={`${overdue.length} past due date`} accent="terra" />
        <KPI label="Not yet due" value={inr(total - overdue.reduce((a, i) => a + i.balance, 0))} sub="Within credit terms" accent="ink" />
      </div>
      <div className="card overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Invoice</th><th>Customer</th><th>Date</th><th>Due</th><th className="num">Total</th><th className="num">Balance</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.map((i) => {
              const late = i.due_date && i.due_date < today;
              const days = late ? Math.round((new Date(today) - new Date(i.due_date)) / 86400000) : 0;
              return (
                <tr key={i.id} className="cursor-pointer" onClick={() => nav(`/invoices/${i.id}`)}>
                  <td className="font-num">{i.invoice_number}</td>
                  <td>{i.customer_name}</td>
                  <td className="text-[#5C626A]">{fmtDate(i.date)}</td>
                  <td className="text-[#5C626A]">{i.due_date ? fmtDate(i.due_date) : "—"}</td>
                  <td className="num font-num">{inr(i.total)}</td>
                  <td className="num font-num text-[#A3411D]">{inr(i.balance)}</td>
                  <td>{late ? <span className="chip border bg-[#FBF0EC] text-[#A3411D] border-[#A3411D]">{days}d overdue</span> : <StatusChip status={i.status} />}</td>
                  <td className="num"><button className="btn btn-ghost btn-sm">Collect</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {list.length === 0 && <Empty>Every invoice is paid up</Empty>}
      </div>
    </div>
  );
}

function Payables() {
  const [list, setList] = useState(null);
  const [paying, setPaying] = useState(null);
  const load = () => api.get("/purchase-orders").then((r) => setList(r.data.filter((p) => p.payable > 0.01)));
  useEffect(() => { load(); }, []);
  if (!list) return null;
  return (
    <div>
      <div className="card overflow-hidden">
        <table className="tbl">
          <thead><tr><th>PO</th><th>Vendor</th><th>Bill no.</th><th>Date</th><th className="num">Received value</th><th className="num">Paid</th><th className="num">Payable</th><th></th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id}>
                <td className="font-num">{p.po_number}</td>
                <td>{p.vendor_name}</td>
                <td className="font-num text-xs text-[#8C929B]">{p.vendor_bill_no || "—"}</td>
                <td className="text-[#5C626A]">{fmtDate(p.order_date)}</td>
                <td className="num font-num">{inr(p.billed)}</td>
                <td className="num font-num text-[#2D5A3C]">{inr(p.amount_paid)}</td>
                <td className="num font-num text-[#A3411D] font-semibold">{inr(p.payable)}</td>
                <td className="num"><button className="btn btn-primary btn-sm" onClick={() => setPaying(p)}>Pay</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <Empty>Nothing owed to vendors</Empty>}
      </div>
      <PayVendorModal po={paying} onClose={() => setPaying(null)} onDone={load} />
    </div>
  );
}

export default function Accounts() {
  const nav = useNavigate();
  const s = useSettings();
  const presets = useMemo(() => periodPresets(s?.fy_start_month || 4), [s?.fy_start_month]);
  const [period, setPeriod] = useState(() => { const p = periodPresets(4)[1]; return { start: p.start, end: p.end }; });
  const [tab, setTab] = useState("overview");
  const [a, setA] = useState(null);
  const [showExpense, setShowExpense] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!period.start || !period.end) return;
    api.get(`/accounts/summary?start=${period.start}&end=${period.end}`).then((r) => setA(r.data));
  }, [period, tick]);

  return (
    <div>
      <PageHeader eyebrow="Books of account" title="Accounts" subtitle="Sales, purchases, expenses, stock and GST in one place — every figure reconciled to invoices, bills and payments.">
        <button className="btn btn-primary" onClick={() => nav("/sales/new")} data-testid="acc-new-invoice"><FilePlus2 size={16} /> New invoice</button>
        <button className="btn btn-ghost" onClick={() => setShowExpense(true)} data-testid="acc-new-expense"><Wallet size={16} /> Expense</button>
        <button className="btn btn-ghost" onClick={() => nav("/purchases?new=1")}><ShoppingBag size={16} /> Purchase</button>
        <button className="btn btn-ghost" onClick={() => nav("/inventory?new=1")}><PackagePlus size={16} /> Product</button>
      </PageHeader>

      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 border-b border-[#E6E1D8]">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`acc-tab-${t.id}`}
              className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === t.id ? "border-[#0E1B2E] text-[#0E1B2E] font-semibold" : "border-transparent text-[#5C626A] hover:text-[#0E1B2E]"}`}>{t.label}</button>
          ))}
        </div>
        {(tab === "overview" || tab === "daybook") && <PeriodPicker presets={presets} value={period} onChange={setPeriod} />}
      </div>

      {tab === "overview" && (a ? <Overview a={a} /> : <div className="text-[#8C929B]">Loading accounts…</div>)}
      {tab === "daybook" && <DayBook key={tick} period={period} />}
      {tab === "receivables" && <Receivables />}
      {tab === "payables" && <Payables />}

      {a && tab === "overview" && (
        <div className="grid grid-cols-2 gap-4 mt-4 text-xs text-[#8C929B]">
          <div className="flex items-center gap-2"><ArrowDownLeft size={14} /> Sales are recognised on the invoice date; GST is shown separately because it belongs to the government.</div>
          <div className="flex items-center gap-2"><ArrowUpRight size={14} /> Purchases count when goods are received; inventory at cost uses the weighted-average cost ({inr0(a.inventory_cost)}).</div>
        </div>
      )}

      <ExpenseForm open={showExpense} onClose={() => setShowExpense(false)} onSaved={() => setTick((t) => t + 1)} />
    </div>
  );
}
